import IconButton from '@material-ui/core/IconButton';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import FavoriteIcon from '@material-ui/icons/Star';
import NonFavoriteIcon from '@material-ui/icons/StarBorder';
import ConfirmationDialog from '@tracker/shared/src/ConfirmationDialog';
import React, {useState} from 'react';
import {Draggable} from 'react-beautiful-dnd';
import {BookmarkLinkWithId} from './Home';
import LinkFormDialog from './LinkFormDialog';

const useStyles = makeStyles(theme => ({
    draggable: {
        cursor: 'grab',
    },
    dragging: {
        cursor: 'grabbing',
    },
    icon: {
        marginLeft: theme.spacing(1),
        fontSize: 14,
    },
}));

type Props = {
    link : BookmarkLinkWithId;
    index : number;
    onUpdate : (link : BookmarkLinkWithId) => Promise<void>;
    onDelete : (link : BookmarkLinkWithId) => Promise<void>;
}

const LinkListItem : React.FC<Props> = ({link, index, onUpdate, onDelete} : Props) => {
    const classes = useStyles();
    const [optionsElement, setOptionsElement] = useState<HTMLElement | null>(null);
    const [updateLinkDialogOpen, setUpdateLinkDialogOpen] = useState(false);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);

    return (
        <>
            <Draggable draggableId={`link-${link.id}`} index={index}>
                {(provided, snapshot) => (
                    <ListItem
                        ref={provided.innerRef}
                        ContainerProps={{
                            ...provided.draggableProps,
                            ...provided.dragHandleProps,
                            style: {...provided.draggableProps.style},
                        }}
                        className={snapshot.isDragging ? classes.dragging : classes.draggable}
                    >
                        <ListItemIcon>
                            {link.favorite ? <FavoriteIcon/> : <NonFavoriteIcon/>}
                        </ListItemIcon>
                        <ListItemText primary={link.title} secondary={(
                            <>
                                {link.url}
                                {link.openInNew && <OpenInNewIcon className={classes.icon}/>}
                            </>
                        )}/>
                        <ListItemSecondaryAction>
                            <IconButton onClick={event => setOptionsElement(event.currentTarget)} edge="end">
                                <MoreVertIcon/>
                            </IconButton>
                            <Menu
                                anchorEl={optionsElement}
                                open={Boolean(optionsElement)}
                                getContentAnchorEl={null}
                                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                                onClose={() => setOptionsElement(null)}
                            >
                                <MenuItem onClick={() => {
                                    setUpdateLinkDialogOpen(true);
                                    setOptionsElement(null);
                                }}>Edit</MenuItem>
                                <MenuItem onClick={() => {
                                    setConfirmDeleteDialogOpen(true);
                                    setOptionsElement(null);
                                }}>Delete</MenuItem>
                            </Menu>
                        </ListItemSecondaryAction>
                    </ListItem>
                )}
            </Draggable>

            <LinkFormDialog
                open={updateLinkDialogOpen}
                link={link}
                onSubmit={onUpdate}
                onClose={() => setUpdateLinkDialogOpen(false)}
            />

            <ConfirmationDialog
                open={confirmDeleteDialogOpen}
                title="Delete link"
                message={`You are about to delete the link "${link.title}" from this link group.`}
                onClose={() => setConfirmDeleteDialogOpen(false)}
                onConfirm={() => onDelete(link)}
                noUpdateAfterConfirm
            />
        </>
    );
};

export default LinkListItem;
